import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import * as authApi from './authApi';
import type { UserSummary } from './authApi';

/**
 * The "who am I" cache key. Using a constant so component tests and
 * mutations can invalidate it without re-typing the key.
 */
export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

/**
 * Read-only view of the signed-in user. Returns `data: null` when
 * there's no session — distinct from `undefined` (not yet loaded).
 *
 * The query is cheap on the server (one indexed cookie lookup) so we
 * let it refetch on remount. It's the source of truth for "is the kid
 * signed in"; everything else reads from this.
 */
export function useCurrentUser(): UseQueryResult<UserSummary | null, Error> {
  return useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: authApi.me,
    staleTime: 60_000,
  });
}

interface SigninArgs {
  email: string;
  password: string;
}

/**
 * Mutation that signs in with email + password. On success it invalidates
 * the `me` query so the rest of the studio re-reads the new user.
 */
export function useSignin(): UseMutationResult<UserSummary, Error, SigninArgs> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: SigninArgs) => authApi.signin(args.email, args.password),
    onSuccess: (user) => {
      qc.setQueryData(AUTH_QUERY_KEY, user);
    },
  });
}

interface SignupArgs extends SigninArgs {
  birthYear: number;
}

/** Same shape as signin plus a required birth year for the COPPA
 *  under-13 gate. The backend hands back a session on signup too, so
 *  the kid lands signed in immediately. */
export function useSignup(): UseMutationResult<UserSummary, Error, SignupArgs> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: SignupArgs) => authApi.signup(args),
    onSuccess: (user) => {
      qc.setQueryData(AUTH_QUERY_KEY, user);
    },
  });
}

export function useSignout(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.signout(),
    onSuccess: () => {
      qc.setQueryData(AUTH_QUERY_KEY, null);
      // Drop other authed-only queries on the way out. We don't have
      // any yet — projects sync lands in M3 — but this is the right
      // hook to add them to.
      void qc.invalidateQueries({ queryKey: ['account'] });
    },
  });
}

/** Permanent account deletion. The server CASCADEs the row removal
 *  through sessions and projects; the cache is torn down the same
 *  way signout does. */
export function useDeleteAccount(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.deleteAccount(),
    onSuccess: () => {
      qc.setQueryData(AUTH_QUERY_KEY, null);
      void qc.invalidateQueries({ queryKey: ['account'] });
    },
  });
}

interface MagicLinkArgs {
  email: string;
}

export function useRequestMagicLink(): UseMutationResult<
  authApi.MagicLinkResponse,
  Error,
  MagicLinkArgs
> {
  return useMutation({
    mutationFn: ({ email }: MagicLinkArgs) => authApi.requestMagicLink(email),
  });
}

export function useConsumeMagicLink(): UseMutationResult<UserSummary, Error, { token: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ token }) => authApi.consumeMagicLink(token),
    onSuccess: (user) => {
      qc.setQueryData(AUTH_QUERY_KEY, user);
    },
  });
}

/** Change the signed-in user's password. The server reissues the
 *  session cookie, so the auth cache stays fresh — but other-device
 *  sessions are invalidated server-side. */
export function useChangePassword(): UseMutationResult<void, Error, authApi.ChangePasswordArgs> {
  return useMutation({
    mutationFn: (args) => authApi.changePassword(args),
  });
}

/** Forgot-password kickoff. */
export function useRequestPasswordReset(): UseMutationResult<
  authApi.PasswordResetResponse,
  Error,
  { email: string }
> {
  return useMutation({
    mutationFn: ({ email }) => authApi.requestPasswordReset(email),
  });
}

/** Consume a reset token + set a new password. The server signs the
 *  kid in as a side effect, so we bump the auth cache directly. */
export function useConsumePasswordReset(): UseMutationResult<
  UserSummary,
  Error,
  authApi.ConsumePasswordResetArgs
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => authApi.consumePasswordReset(args),
    onSuccess: (user) => {
      qc.setQueryData(AUTH_QUERY_KEY, user);
    },
  });
}

// --- Parent-linked accounts (Phase 4.5a) -------------------------------

const CHILDREN_QUERY_KEY = ['account', 'children'] as const;

export function useChildren(options?: {
  enabled?: boolean;
}): import('@tanstack/react-query').UseQueryResult<authApi.ChildSummary[], Error> {
  const { data: user } = useCurrentUser();
  const callerEnabled = options?.enabled ?? true;
  return useQuery({
    queryKey: CHILDREN_QUERY_KEY,
    queryFn: authApi.listChildren,
    // Only enabled for signed-in adult parents AND when the caller
    // opts in. The caller gate stops mounted-but-hidden dialogs
    // (ChildrenDialog returns null while closed but its hooks still
    // run) from pre-fetching unnecessary network calls.
    enabled: callerEnabled && Boolean(user) && user?.is_child !== true,
    staleTime: 30_000,
  });
}

export function useAddChild(): UseMutationResult<
  authApi.ChildSummary,
  Error,
  authApi.AddChildArgs
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => authApi.addChild(args),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHILDREN_QUERY_KEY });
    },
  });
}

export function useDeleteChild(): UseMutationResult<void, Error, { childId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ childId }) => authApi.deleteChild(childId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHILDREN_QUERY_KEY });
    },
  });
}

export function useChildSignin(): UseMutationResult<UserSummary, Error, authApi.ChildSigninArgs> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => authApi.childSignin(args),
    onSuccess: (user) => {
      qc.setQueryData(AUTH_QUERY_KEY, user);
      void qc.invalidateQueries({ queryKey: CHILDREN_QUERY_KEY });
    },
  });
}

/** Sign a kid in to a teacher-managed classroom. Bumps the auth
 *  cache directly so the studio re-renders for the new user
 *  without a round-trip to /auth/me. */
export function useStudentSignin(): UseMutationResult<
  UserSummary,
  Error,
  authApi.StudentSigninArgs
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => authApi.studentSignin(args),
    onSuccess: (user) => {
      qc.setQueryData(AUTH_QUERY_KEY, user);
    },
  });
}
